import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from "@angular/core";
import { ToastrService } from "ngx-toastr";
import { forkJoin, of, Subject } from "rxjs";
import { catchError, debounceTime, distinctUntilChanged, switchMap } from "rxjs/operators";
import {
  DispatchScheduleService,
  ClientSchedulingTeamGroup,
  DriverAssignmentRow,
  DriverDayData,
  NoteIconState,
} from "../shared/dispatch-schedule.service";
import {
  DispatchService,
  SchedulingTeamForAssignments,
  SchedulingTeamDriverAssignment,
  SchedulingTeamDispatchContact,
} from "../shared/dispatch.service";
import { DriverService } from "@app/driver/shared/driver.service";
import { GetPictureService } from "@app/shared/services/files/getPictures.service";
import {
  DriverEventChangeViewModel,
  DriverEventChangeReason,
  DriverEventChangeReasonHelper,
} from "@app/shared/models/drivers/DriverEventViewModel.model";
import { UserPreferencesService } from "../shared/user-preferences.service";
import { StatesServices } from "@app/shared/services/states/states.service";
import { NewTripService } from "@app/trip-new/shared/trip.service";
import { DriverNote, DriverViewModel } from "@app/shared/models/drivers/DriverViewModel.model";

import { DispatchDashboardService } from "@app/dispatch-dashboard/dispatch-dashboard.service";

const EMPTY_NOTE_ICON: NoteIconState = { hasIcon: false, resolved: false, tooltip: "" };

function computeNoteIcon(
  notes: { type: string; note: string; status: string }[] | undefined,
  type: string
): NoteIconState {
  const matching = notes?.filter((n) => n.type === type);
  if (!matching || matching.length === 0) return EMPTY_NOTE_ICON;
  const resolved = matching.every((n) => n.status === "Resolved");
  const statusLabel = resolved ? "Resolved" : "Unresolved";
  const noteText = matching.map((n) => n.note).join("\n");
  return {
    hasIcon: true,
    resolved,
    tooltip: noteText ? `${type} [${statusLabel}]\n${noteText}` : `${type} [${statusLabel}]`,
  };
}

@Component({
  selector: "app-dispatch-driver-assignments",
  templateUrl: "./driver-assignments.component.html",
  styleUrls: ["./driver-assignments.component.css"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DriverAssignmentsTabComponent implements OnInit, OnDestroy {
  private searchSubject = new Subject<string>();
  private noteSaveSubject = new Subject<{ driverId: string; note: string | null }>();
  noteSavingDriverId: string | null = null;
  noteSavedDriverId: string | null = null;
  private noteSavedTimer: any = null;
  isLoading = true;
  clientGroups: ClientSchedulingTeamGroup[] = [];
  schedulingTeamGroups: {
    schedulingTeamId: string;
    schedulingTeamName: string;
    clientName: string;
    dispatchContacts: SchedulingTeamDispatchContact[];
    drivers: DriverAssignmentRow[];
  }[] = [];
  driverRows: DriverAssignmentRow[] = [];
  filteredDriverRows: DriverAssignmentRow[] = [];
  filteredSchedulingTeamGroups: {
    schedulingTeamId: string;
    schedulingTeamName: string;
    clientName: string;
    dispatchContacts: SchedulingTeamDispatchContact[];
    drivers: DriverAssignmentRow[];
  }[] = [];
  expandedSchedulingTeamIds: Set<string> = new Set();

  // Unassigned drivers (not in any scheduling team)
  unassignedDriverRows: DriverAssignmentRow[] = [];
  filteredUnassignedDriverRows: DriverAssignmentRow[] = [];
  unassignedGroupExpanded = true;

  // Column visibility
  readonly columnPreferenceKey = "dispatch-driver-assignments-columns";
  showColumnPicker = false;
  driverInfoColumns: { key: string; label: string; visible: boolean }[] = [
    { key: "driverNo", label: "Driver #", visible: true },
    { key: "peopleNetId", label: "PeopleNet ID", visible: true },
    { key: "autoTrans", label: "Auto", visible: true },
    { key: "doNotDispatch", label: "Do Not Dispatch", visible: true },
    { key: "team", label: "Team", visible: true },
    { key: "boardBillTo", label: "Board / Bill-To", visible: true },
    { key: "equipment", label: "Equipment", visible: true },
    { key: "phone", label: "Phone Number", visible: true },
    { key: "homeLocation", label: "Home Location", visible: true },
    { key: "currentLocation", label: "Current Location", visible: true },
    { key: "note", label: "Note", visible: true },
  ];
  // O(1) column visibility lookup - updated via onColumnVisibilityChange()
  columnVisible: { [key: string]: boolean } = Object.fromEntries(
    this.driverInfoColumns.map((c) => [c.key, c.visible])
  );

  // Driver Note modal
  showDriverNoteModal = false;
  driverNoteForModal: Partial<DriverNote> = {};
  driverViewModelForNoteModal: DriverViewModel | null = null;
  noteModalTractors: any[] = [];
  noteModalTrailers: any[] = [];
  noteModalClients: any[] = [];
  noteModalAllClients: any[] = [];
  noteModalProducts: any[] = [];
  noteModalStates: any[] = [];
  noteModalDropdownsLoaded = false;

  // Date columns (starting from 7 days from yesterday, expandable)
  dateColumns: Date[] = [];
  numberOfWeeks = 2;

  // Filters
  searchText: string = "";

  // Date click modal
  showDateClickModal = false;
  selectedDriver: SchedulingTeamDriverAssignment | null = null;
  selectedDate: Date | null = null;
  selectedSchedulingTeamId: string | null = null;
  selectedClientId: string | null = null;
  selectedClientName: string | null = null;
  selectedDayData: DriverDayData | null = null;

  // Trip modal
  showTripModal = false;
  tripModalDriverId: string | null = null;
  tripModalClientId: string | null = null;
  tripModalBillToId: string | null = null;
  tripModalTractorId: string | null = null;
  tripModalTrailerId: string | null = null;
  tripModalProductId: string | null = null;
  tripModalStartDate: Date | null = null;
  editTripId: string | null = null;

  // View Note Modal
  showViewNoteModal = false;
  viewingNote: { type: string; note: string } | null = null;

  // Time off form
  timeOffEventType = "TimeOff";
  timeOffNotes = "";
  eventTypeOptions = [
    { label: "Sick", value: "Sick" },
    { label: "Home Time", value: "HomeTime" },
    { label: "PTO", value: "PTO" },
    { label: "Time Off", value: "TimeOff" },
  ];

  // Time off modal
  showTimeOffModal = false;
  timeOffRecord: any = {
    id: null,
    startDate: null,
    endDate: null,
    eventType: "TimeOff",
    notes: "",
  };
  isEditingTimeOff = false;

  // Change reason dialog
  displayChangeDialog = false;
  changeReason: DriverEventChangeReason = DriverEventChangeReason.DriverRequested;
  changeReasonNote = "";
  originalStartDate: Date = new Date();
  originalEndDate: Date = new Date();
  pendingUpdate: any = null;

  // Assign to scheduling team modal
  showAssignModal = false;
  assignModalRow: DriverAssignmentRow | null = null;
  assignModalDriverId: string | null = null;
  assignModalExistingTeamId: string | null = null;
  assignModalExistingBoardId: string | null = null;
  assignModalExistingBillToId: string | null = null;
  // Pending unassign (triggered by Driver Release note flow)
  pendingUnassignTeamId: string | null = null;

  // Time off prompt (shown after saving Driver Release / Accident / Breakdown note)
  showTimeOffPromptModal = false;
  pendingTimeOffDriverId: string | null = null;
  pendingTimeOffDriverName: string | null = null;

  constructor(
    private dispatchScheduleService: DispatchScheduleService,
    private dispatchService: DispatchService,
    private driverService: DriverService,
    private toastr: ToastrService,
    private getPictureService: GetPictureService,
    private userPreferencesService: UserPreferencesService,
    private statesService: StatesServices,
    private newTripService: NewTripService,
    private dashboardService: DispatchDashboardService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.initializeDateColumns();
    this.loadFilterOptions();
    this.loadData();
    this.loadColumnPreferences();

    // Set up search debounce
    this.searchSubject.pipe(debounceTime(300), distinctUntilChanged()).subscribe(() => {
      this.applyFilters();
      this.cdr.markForCheck();
    });

    // Set up note autosave
    this.noteSaveSubject
      .pipe(
        debounceTime(800),
        switchMap(({ driverId, note }) => {
          this.noteSavingDriverId = driverId;
          this.cdr.markForCheck();
          return this.driverService.updateSpecialNote(driverId, note).pipe(
            catchError((err) => {
              console.error("Failed to autosave note", err);
              this.toastr.error("Failed to save note", "Error");
              return of(null);
            })
          );
        })
      )
      .subscribe(() => {
        const savedId = this.noteSavingDriverId;
        this.noteSavingDriverId = null;
        this.noteSavedDriverId = savedId;
        this.cdr.markForCheck();
        clearTimeout(this.noteSavedTimer);
        this.noteSavedTimer = setTimeout(() => {
          this.noteSavedDriverId = null;
          this.cdr.markForCheck();
        }, 2000);
      });
  }

  ngOnDestroy(): void {
    this.searchSubject.complete();
    this.noteSaveSubject.complete();
  }

  trackByDriverId(_index: number, row: DriverAssignmentRow): string {
    return row.driver.driverId;
  }

  trackByDate(_index: number, dayData: DriverDayData): number {
    return dayData.date.getTime();
  }

  trackBySchedulingTeamId(_index: number, group: { schedulingTeamId: string }): string {
    return group.schedulingTeamId;
  }

  onSearchTextChange(): void {
    this.searchSubject.next(this.searchText);
  }

  initializeDateColumns(): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    this.dateColumns = [];
    const totalDays = this.numberOfWeeks * 7;
    for (let i = 0; i < totalDays; i++) {
      const date = new Date(today);
      date.setDate(date.getDate() + i);
      this.dateColumns.push(date);
    }
  }

  loadFilterOptions(): void {
    // No longer needed with text search
  }

  loadData(): void {
    this.isLoading = true;

    forkJoin({
      teams: this.dispatchService.getSchedulingTeamsWithDrivers(),
      unassigned: this.dispatchService.getDriversWithoutSchedulingTeam(),
    }).subscribe({
      next: ({ teams, unassigned }) => {
        this.buildClientGroups(teams);
        this.buildDriverRows(teams);
        this.buildUnassignedDriverRows(unassigned);
        this.loadDriverDayData(); // Logic inside will use both driverRows and unassignedDriverRows
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        this.toastr.error("Failed to load driver assignments");
        console.error(error);
        this.isLoading = false;
        this.cdr.markForCheck();
      },
    });
  }

  buildClientGroups(schedulingTeams: SchedulingTeamForAssignments[]): void {
    const clientMap = new Map<string, ClientSchedulingTeamGroup>();
    // ...
    schedulingTeams.forEach((team) => {
      // clientName can be null from server, but we handle it
      const cId = team.clientId || "unknown"; // robust fallback
      if (!clientMap.has(cId)) {
        clientMap.set(cId, {
          clientId: cId,
          clientName: team.clientName || "Unknown Client",
          schedulingTeams: [],
        });
      }
      clientMap.get(cId)!.schedulingTeams.push(team);
    });

    this.clientGroups = Array.from(clientMap.values()).sort((a, b) =>
      a.clientName.localeCompare(b.clientName)
    );
  }

  buildSchedulingTeamGroups(): void {
    // ...
    const groupMap = new Map<
      string,
      {
        schedulingTeamId: string;
        schedulingTeamName: string;
        clientName: string;
        dispatchContacts: SchedulingTeamDispatchContact[];
        drivers: DriverAssignmentRow[];
      }
    >();

    this.driverRows.forEach((row) => {
      if (!groupMap.has(row.schedulingTeamId)) {
        groupMap.set(row.schedulingTeamId, {
          schedulingTeamId: row.schedulingTeamId,
          schedulingTeamName: row.schedulingTeamName,
          clientName: row.clientName,
          dispatchContacts: row.dispatchContacts,
          drivers: [],
        });
      }
      groupMap.get(row.schedulingTeamId)!.drivers.push(row);
    });

    this.schedulingTeamGroups = Array.from(groupMap.values()).sort((a, b) =>
      a.schedulingTeamName.localeCompare(b.schedulingTeamName)
    );

    // Expand all scheduling teams by default
    this.schedulingTeamGroups.forEach((group) => {
      this.expandedSchedulingTeamIds.add(group.schedulingTeamId);
    });

    // We don't call applyFilters here to avoid double calling,
    // but the original code did. We can leave it.
    this.applyFilters();
  }

  buildDriverRows(schedulingTeams: SchedulingTeamForAssignments[]): void {
    this.driverRows = [];

    schedulingTeams.forEach((team) => {
      team.drivers.forEach((driver) => {
        const days: DriverDayData[] = this.dateColumns.map((date) => ({
          date: date,
          isTimeOff: false,
          timeOffType: null,
          timeOffEventId: null,
          tripCount: 0,
          boardName: driver.boardName,
          trips: [],
          accidentIcon: EMPTY_NOTE_ICON,
          incidentIcon: EMPTY_NOTE_ICON,
          breakdownIcon: EMPTY_NOTE_ICON,
        }));

        this.driverRows.push({
          driver,
          schedulingTeamId: team.id,
          schedulingTeamName: team.name,
          clientId: team.clientId || "",
          clientName: team.clientName || "",
          dispatchContacts: team.dispatchContacts || [],
          days,
          openNoteCount: 0,
          displayName: this.getDriverDisplayName(driver),
          pictureUrl: this.getDriverPictureUrl(driver),
          matchedFieldSet: new Set<string>(),
        });
      });
    });

    this.buildSchedulingTeamGroups();
  }

  buildUnassignedDriverRows(drivers: SchedulingTeamDriverAssignment[]): void {
    this.unassignedDriverRows = drivers.map((driver) => ({
      driver,
      schedulingTeamId: "__unassigned__",
      schedulingTeamName: "Unassigned",
      clientId: "",
      clientName: "",
      dispatchContacts: [],
      days: this.dateColumns.map((date) => ({
        date,
        isTimeOff: false,
        timeOffType: null,
        timeOffEventId: null,
        tripCount: 0,
        boardName: null,
        trips: [],
        accidentIcon: EMPTY_NOTE_ICON,
        incidentIcon: EMPTY_NOTE_ICON,
        breakdownIcon: EMPTY_NOTE_ICON,
      })),
      openNoteCount: 0,
      displayName: this.getDriverDisplayName(driver),
      pictureUrl: this.getDriverPictureUrl(driver),
      matchedFieldSet: new Set<string>(),
    }));
    this.filteredUnassignedDriverRows = [...this.unassignedDriverRows];
  }

  loadDriverDayData(): void {
    const allRows = [...this.driverRows, ...this.unassignedDriverRows];
    const driverIds = allRows.map((r) => r.driver.driverId);
    if (driverIds.length === 0) {
      this.isLoading = false;
      return;
    }

    const startDate = this.dateColumns[0];
    const endDate = this.dateColumns[this.dateColumns.length - 1];

    // Extend time off lookback by 10 days to capture recent HosReset34h events
    // whose projected 7-day restart may fall within the visible range
    const timeOffStartDate = new Date(startDate);
    timeOffStartDate.setDate(timeOffStartDate.getDate() - 10);

    forkJoin({
      timeOff: this.dispatchService
        .getDriverTimeOffForDateRange(driverIds, timeOffStartDate, endDate)
        .pipe(
          catchError((err) => {
            console.error("Failed to load time off data", err);
            return of({});
          })
        ),
      tripCounts: this.dispatchService
        .getDriverTripCountsForDateRange(driverIds, startDate, endDate)
        .pipe(
          catchError((err) => {
            console.error("Failed to load trip counts", err);
            return of({});
          })
        ),
      notes: this.dispatchService
        .getDriverAccidentsAndBreakdownsForDateRange(driverIds, startDate, endDate)
        .pipe(
          catchError((err) => {
            console.error("Failed to load driver notes", err);
            return of({});
          })
        ),
      openNoteCounts: this.dispatchService.getDriverOpenNoteCounts(driverIds).pipe(
        catchError((err) => {
          console.error("Failed to load open note counts", err);
          return of({} as { [key: string]: number });
        })
      ),
    }).subscribe({
      next: (data) => {
        // Apply time off data
        const allRows = [...this.driverRows, ...this.unassignedDriverRows];
        Object.entries(data.timeOff).forEach(([driverId, timeOffDays]) => {
          const row = allRows.find((r) => r.driver.driverId === driverId);
          if (row) {
            timeOffDays.forEach((to) => {
              // Parse date properly to avoid timezone issues
              const dateParts = to.date.split("-");
              const dateObj = new Date(
                parseInt(dateParts[0]),
                parseInt(dateParts[1]) - 1,
                parseInt(dateParts[2])
              );
              const dayIndex = this.getDayIndex(dateObj);
              if (dayIndex >= 0 && dayIndex < row.days.length) {
                row.days[dayIndex].isTimeOff = true;
                row.days[dayIndex].timeOffType = to.eventType;
                row.days[dayIndex].timeOffEventId = to.id || null;
              }
            });
          }
        });

        // Compute projected 34h restart estimates for each row
        allRows.forEach((row) => {
          // Find the most recent HosReset34h date across all returned time off days
          // (including days before the visible range from the extended lookback)
          let lastResetDate: Date | null = null;
          Object.entries(data.timeOff).forEach(([driverId, timeOffDays]) => {
            if (driverId !== row.driver.driverId) return;
            (timeOffDays as any[])
              .filter((to) => to.eventType === "HosReset34h")
              .forEach((to) => {
                const parts = to.date.split("-");
                const d = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
                if (!lastResetDate || d > lastResetDate) lastResetDate = d;
              });
          });
          if (lastResetDate) {
            const projected = new Date(lastResetDate);
            projected.setDate(projected.getDate() + 7);
            const dayIndex = this.getDayIndex(projected);
            if (dayIndex >= 0 && dayIndex < row.days.length && !row.days[dayIndex].isTimeOff) {
              row.days[dayIndex].isEstimate34h = true;
            }
          }
        });

        // Apply trip counts
        Object.entries(data.tripCounts).forEach(([driverId, tripDays]) => {
          const row = allRows.find((r) => r.driver.driverId === driverId);
          if (row) {
            tripDays.forEach((tc) => {
              // Parse date properly to avoid timezone issues
              const dateParts = tc.date.split("-");
              const dateObj = new Date(
                parseInt(dateParts[0]),
                parseInt(dateParts[1]) - 1,
                parseInt(dateParts[2])
              );
              const dayIndex = this.getDayIndex(dateObj);
              if (dayIndex >= 0 && dayIndex < row.days.length) {
                row.days[dayIndex].tripCount = tc.count;
                row.days[dayIndex].trips = tc.trips || [];
              }
            });
          }
        });

        // Apply driver notes
        Object.entries(data.notes).forEach(([driverId, noteList]) => {
          const row = allRows.find((r) => r.driver.driverId === driverId);
          if (row) {
            (noteList as any[]).forEach((n) => {
              // Use accidentDate if available (filtered for "Accident" or "Breakdown & follow up")
              // otherwise fall back to creation date
              const dateSource = n.accidentDate || n.date;
              if (!dateSource) return;

              const dateParts = dateSource.split("-");
              const dateObj = new Date(
                parseInt(dateParts[0]),
                parseInt(dateParts[1]) - 1,
                parseInt(dateParts[2])
              );
              const dayIndex = this.getDayIndex(dateObj);
              if (dayIndex >= 0 && dayIndex < row.days.length) {
                if (!row.days[dayIndex].notes) {
                  row.days[dayIndex].notes = [];
                }
                row.days[dayIndex].notes!.push(n);
              }
            });
          }
        });

        // Apply open note counts and recompute note icons now that notes are populated
        allRows.forEach((row) => {
          row.openNoteCount = data.openNoteCounts[row.driver.driverId] ?? 0;
          row.days.forEach((day) => {
            if (day.notes && day.notes.length > 0) {
              day.accidentIcon = computeNoteIcon(day.notes, "Accident");
              day.incidentIcon = computeNoteIcon(day.notes, "Incident");
              day.breakdownIcon = computeNoteIcon(day.notes, "Breakdown & follow up");
            }
          });
        });

        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error("Failed to load driver day data", error);
        this.isLoading = false;
        this.cdr.markForCheck();
        this.toastr.error("Failed to load schedule data");
      },
    });
  }

  getDayIndex(date: Date): number {
    const dateStr = this.formatDateKey(date);
    return this.dateColumns.findIndex((d) => this.formatDateKey(d) === dateStr);
  }

  formatDateKey(date: Date): string {
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    return `${date.getFullYear()}-${month}-${day}`;
  }

  formatPhone(phone: string | null): string {
    if (!phone) return "";
    const digits = phone.replace(/\D/g, "");
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    } else if (digits.length === 11 && digits[0] === "1") {
      return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
    return phone;
  }

  hasNoteType(
    notes: { id: string; type: string; note: string; status: string }[] | undefined,
    type: string
  ): boolean {
    return !!notes?.some((n) => n.type === type);
  }

  isNoteTypeResolved(
    notes: { id: string; type: string; note: string; status: string }[] | undefined,
    type: string
  ): boolean {
    const matching = notes?.filter((n) => n.type === type);
    return !!matching && matching.length > 0 && matching.every((n) => n.status === "Resolved");
  }

  getNoteTypeTooltip(
    notes: { id: string; type: string; note: string; status: string }[] | undefined,
    type: string
  ): string {
    if (!notes) return "";
    const matching = notes.filter((n) => n.type === type);
    if (matching.length === 0) return type;
    const allResolved = matching.every((n) => n.status === "Resolved");
    const statusLabel = allResolved ? "Resolved" : "Unresolved";
    const noteText = matching.map((n) => n.note).join("\n");
    return noteText ? `${type} [${statusLabel}]\n${noteText}` : `${type} [${statusLabel}]`;
  }

  applyFilters(): void {
    const searchLower = this.searchText.toLowerCase().trim();

    if (!searchLower) {
      this.driverRows.forEach((row) => (row.matchedFieldSet = new Set<string>()));
      this.unassignedDriverRows.forEach((row) => (row.matchedFieldSet = new Set<string>()));
      this.filteredDriverRows = [...this.driverRows];
      this.filteredSchedulingTeamGroups = this.schedulingTeamGroups
        .map((group) => ({
          ...group,
          drivers: group.drivers.filter((d) => this.driverRows.includes(d)),
        }))
        .filter((group) => group.drivers.length > 0);
      this.filteredUnassignedDriverRows = [...this.unassignedDriverRows];
      return;
    }

    this.filteredDriverRows = this.driverRows.filter((row) => {
      const driver = row.driver;
      const matched = new Set<string>();

      const fieldsToSearch = [
        { value: driver.name, label: "name" },
        { value: driver.lastName, label: "lastName" },
        { value: row.displayName, label: "name" },
        { value: driver.driverNo, label: "driverNo" },
        { value: driver.cellphone, label: "cellphone" },
        { value: driver.homeLocation, label: "location" },
        { value: driver.boardName, label: "board" },
        { value: driver.billToClientName, label: "billTo" },
        { value: driver.peopleNetId, label: "peopleNetId" },
        { value: driver.tractorNumber, label: "tractor" },
        { value: driver.trailerNumber, label: "trailer" },
        { value: driver.specialNote, label: "specialNote" },
        { value: row.schedulingTeamName, label: "schedulingTeam" },
      ];

      let matches = fieldsToSearch.some((field) => {
        if (field.value && field.value.toString().toLowerCase().includes(searchLower)) {
          matched.add(field.label);
          return true;
        }
        return false;
      });

      if (!matches && driver.autoTransmissionOnly && "auto".includes(searchLower)) {
        matched.add("autoTrans");
        matches = true;
      }

      if (!matches && driver.teamMembers?.some((m: string) => m.toLowerCase().includes(searchLower))) {
        matched.add("team");
        matches = true;
      }

      row.matchedFieldSet = matched;
      return matches;
    });

    // Rebuild scheduling team groups with filtered drivers
    this.filteredSchedulingTeamGroups = this.schedulingTeamGroups
      .map((group) => ({
        ...group,
        drivers: group.drivers.filter((d) => this.filteredDriverRows.includes(d)),
      }))
      .filter((group) => group.drivers.length > 0);

    // Filter unassigned driver rows
    this.filteredUnassignedDriverRows = this.unassignedDriverRows.filter((row) => {
      const driver = row.driver;
      const matched = new Set<string>();

      const fieldsToSearch = [
        { value: driver.name, label: "name" },
        { value: driver.lastName, label: "lastName" },
        { value: row.displayName, label: "name" },
        { value: driver.driverNo, label: "driverNo" },
        { value: driver.cellphone, label: "cellphone" },
        { value: driver.peopleNetId, label: "peopleNetId" },
        { value: driver.tractorNumber, label: "tractor" },
        { value: driver.trailerNumber, label: "trailer" },
        { value: driver.specialNote, label: "specialNote" },
      ];

      let matches = fieldsToSearch.some((field) => {
        if (field.value && field.value.toString().toLowerCase().includes(searchLower)) {
          matched.add(field.label);
          return true;
        }
        return false;
      });

      if (!matches && driver.autoTransmissionOnly && "auto".includes(searchLower)) {
        matched.add("autoTrans");
        matches = true;
      }

      if (!matches && driver.teamMembers?.some((m: string) => m.toLowerCase().includes(searchLower))) {
        matched.add("team");
        matches = true;
      }

      row.matchedFieldSet = matched;
      return matches;
    });
  }

  clearFilters(): void {
    this.searchText = "";
    this.applyFilters();
  }

  exportExcel(): void {
    import("xlsx").then((xlsx) => {
      const rows: any[] = [];

      const formatDate = (d: Date) => {
        const m = d.getMonth() + 1;
        const day = d.getDate();
        const y = d.getFullYear();
        return `${m}/${day}/${y}`;
      };

      const buildRow = (row: DriverAssignmentRow, teamName: string) => {
        const base: any = {
          "Scheduling Team": teamName,
          "Driver #": row.driver.driverNo ?? "",
          Name: row.displayName,
          PeopleNet: row.driver.peopleNetId ?? "",
          Auto: row.driver.autoTransmissionOnly ? "Yes" : "",
          Team: (row.driver.teamMembers ?? []).join(", "),
          Board: row.driver.boardName ?? "",
          "Bill-To": row.driver.billToClientName ?? "",
          Tractor: row.driver.tractorNumber ?? "",
          Trailer: row.driver.trailerNumber ?? "",
          Phone: row.driver.cellphone ?? "",
          "Home Location": row.driver.homeLocation ?? "",
          Note: row.driver.specialNote ?? "",
        };
        for (const date of this.dateColumns) {
          const key = formatDate(date);
          const dayData = row.days.find(
            (d) => new Date(d.date).toDateString() === date.toDateString()
          );
          if (!dayData) {
            base[key] = "";
          } else if (dayData.isTimeOff) {
            base[key] = "OFF";
          } else if (dayData.tripCount > 0) {
            base[key] = dayData.trips.map((t) => t.billToName ?? t.clientName ?? "").join("; ");
          } else {
            base[key] = "";
          }
        }
        return base;
      };

      for (const group of this.filteredSchedulingTeamGroups) {
        for (const row of group.drivers) {
          rows.push(buildRow(row, group.schedulingTeamName));
        }
      }
      for (const row of this.filteredUnassignedDriverRows) {
        rows.push(buildRow(row, "Unassigned"));
      }

      const worksheet = xlsx.utils.json_to_sheet(rows);
      const workbook = xlsx.utils.book_new();
      xlsx.utils.book_append_sheet(workbook, worksheet, "Driver Assignments");
      xlsx.writeFile(workbook, `Driver_Assignments_${new Date().toISOString().split("T")[0]}.xlsx`);
    });
  }

  toggleSchedulingTeamExpansion(schedulingTeamId: string): void {
    if (this.expandedSchedulingTeamIds.has(schedulingTeamId)) {
      this.expandedSchedulingTeamIds.delete(schedulingTeamId);
    } else {
      this.expandedSchedulingTeamIds.add(schedulingTeamId);
    }
  }

  toggleUnassignedGroup(): void {
    this.unassignedGroupExpanded = !this.unassignedGroupExpanded;
  }

  isSchedulingTeamExpanded(schedulingTeamId: string): boolean {
    return this.expandedSchedulingTeamIds.has(schedulingTeamId);
  }

  formatDateHeader(date: Date): string {
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const day = days[date.getDay()];
    const month = date.getMonth() + 1;
    const dayNum = date.getDate();
    return `${day} ${month}/${dayNum}`;
  }

  isToday(date: Date): boolean {
    const today = new Date();
    return (
      date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate()
    );
  }

  isYesterday(date: Date): boolean {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return (
      date.getFullYear() === yesterday.getFullYear() &&
      date.getMonth() === yesterday.getMonth() &&
      date.getDate() === yesterday.getDate()
    );
  }

  onDateCellClick(row: DriverAssignmentRow, dayData: DriverDayData): void {
    this.selectedDriver = row.driver;
    this.selectedDate = dayData.date;
    this.selectedSchedulingTeamId = row.schedulingTeamId;
    this.selectedClientId = row.clientId;
    this.selectedClientName = row.clientName;
    this.selectedDayData = dayData;

    // Check if there's already a time off event for this day
    if (dayData.isTimeOff) {
      // Open time off modal in edit mode
      this.openTimeOffModalForEdit(dayData);
    } else {
      // Open selection modal
      this.timeOffEventType = "TimeOff";
      this.timeOffNotes = "";
      this.showDateClickModal = true;
    }
  }

  closeDateClickModal(): void {
    this.showDateClickModal = false;
    this.selectedDriver = null;
    this.selectedDate = null;
    this.selectedDayData = null;
  }

  // Legacy method - kept for compatibility but redirects to new modal
  createTimeOff(): void {
    this.openTimeOffModal();
  }

  openTripModalFromDateClick(): void {
    if (!this.selectedDriver || !this.selectedDate) return;

    this.tripModalDriverId = this.selectedDriver.driverId;
    this.tripModalClientId = this.selectedClientId;
    this.tripModalBillToId = this.selectedDriver.billToClientId;
    this.tripModalTractorId = this.selectedDriver.tractorId;
    this.tripModalTrailerId = this.selectedDriver.trailerId;
    this.tripModalProductId = this.selectedDriver.productId;
    this.tripModalStartDate = this.selectedDate;

    this.closeDateClickModal();
    this.editTripId = null;
    this.showTripModal = true;
  }

  openTripForEdit(tripId: string): void {
    this.editTripId = tripId;
    this.closeDateClickModal();
    this.showTripModal = true;
  }

  onTripModalSaved(): void {
    this.showTripModal = false;
    this.loadData(); // Refresh data
  }

  onTripModalCancelled(): void {
    this.showTripModal = false;
  }

  getDriverDisplayName(driver: SchedulingTeamDriverAssignment): string {
    return `${driver.name} ${driver.lastName}`;
  }

  getDriverPictureUrl(driver: SchedulingTeamDriverAssignment): string {
    if (driver.picture) {
      return this.getPictureService.getPicturePath(driver.picture, "driver");
    }
    return "assets/img/tractordriver.jpg";
  }

  expandWeek(): void {
    this.numberOfWeeks++;
    this.initializeDateColumns();
    // Rebuild driver rows with new date columns
    if (this.driverRows.length > 0) {
      this.buildDriverRows(this.clientGroups.map((g) => g.schedulingTeams).flat());
      this.loadDriverDayData();
    }
  }

  getBoardDisplayText(driver: SchedulingTeamDriverAssignment): string {
    const parts: string[] = [];
    if (driver.boardName) parts.push(driver.boardName);
    if (driver.billToClientName) parts.push(driver.billToClientName);
    return parts.length > 0 ? parts.join(" / ") : "-";
  }

  openTimeOffModal(): void {
    this.isEditingTimeOff = false;
    const startDate = new Date(this.selectedDate!);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(startDate);

    this.timeOffRecord = {
      id: null,
      startDate: startDate,
      endDate: endDate,
      eventType: "TimeOff",
      notes: "",
    };
    this.showTimeOffModal = true;
    this.showDateClickModal = false;
  }

  openTimeOffModalForEdit(dayData: DriverDayData): void {
    this.isEditingTimeOff = true;

    if (dayData.timeOffEventId) {
      // Load the actual time off event from the server
      this.dispatchScheduleService.getDriverTimeOffEvent(dayData.timeOffEventId).subscribe({
        next: (event: any) => {
          // Map enum value or string to match dropdown options
          let eventType = event.eventType;
          if (typeof eventType === "number") {
            // If it's an enum number, map it
            const enumMap: any = { 0: "Sick", 1: "HomeTime", 2: "PTO", 3: "TimeOff" };
            eventType = enumMap[eventType] || "TimeOff";
          } else if (typeof eventType === "string") {
            // Remove spaces for consistency with dropdown values
            eventType = eventType.replace(/\s+/g, "");
          }

          this.timeOffRecord = {
            id: event.id,
            startDate: new Date(event.start),
            endDate: new Date(event.end),
            eventType: eventType,
            notes: event.notes || "",
          };
          this.showTimeOffModal = true;
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error("Error loading time off event:", error);
          this.toastr.error("Failed to load time off event details", "Error");
          // Fallback to basic record
          this.timeOffRecord = {
            id: null,
            startDate: dayData.date,
            endDate: dayData.date,
            eventType: dayData.timeOffType || "TimeOff",
            notes: "",
          };
          this.showTimeOffModal = true;
          this.cdr.markForCheck();
        },
      });
    } else {
      // Fallback for cases where event ID is not available
      this.timeOffRecord = {
        id: null,
        startDate: dayData.date,
        endDate: dayData.date,
        eventType: dayData.timeOffType || "TimeOff",
        notes: "",
      };
      this.showTimeOffModal = true;
    }
  }

  closeTimeOffModal(): void {
    this.showTimeOffModal = false;
    this.timeOffRecord = {
      id: null,
      startDate: null,
      endDate: null,
      eventType: "TimeOff",
      notes: "",
    };
  }

  saveTimeOffRecord(): void {
    // Validate required fields
    if (!this.timeOffRecord.eventType || this.timeOffRecord.eventType.trim() === "") {
      this.toastr.warning("Event Type is required", "Validation Error");
      return;
    }

    if (!this.timeOffRecord.notes || this.timeOffRecord.notes.trim() === "") {
      this.toastr.warning("Notes are required", "Validation Error");
      return;
    }

    if (!this.selectedDriver) {
      this.toastr.error("No driver selected", "Error");
      return;
    }

    // Check if dates have changed (when editing)
    if (this.isEditingTimeOff && this.timeOffRecord.id) {
      // Load the original time off event to compare dates
      this.dispatchScheduleService.getDriverTimeOffEvent(this.timeOffRecord.id).subscribe({
        next: (originalEvent: any) => {
          const originalStart = new Date(originalEvent.start);
          const originalEnd = new Date(originalEvent.end);
          const newStart = new Date(this.timeOffRecord.startDate);
          const newEnd = new Date(this.timeOffRecord.endDate);

          const startChanged = !this.areDatesEqual(originalStart, newStart);
          const endChanged = !this.areDatesEqual(originalEnd, newEnd);

          if (startChanged || endChanged) {
            // Store original dates and new record for later
            this.originalStartDate = originalStart;
            this.originalEndDate = originalEnd;
            this.pendingUpdate = { ...this.timeOffRecord };

            // Show change reason dialog
            this.displayChangeDialog = true;
            this.cdr.markForCheck();
            return;
          } else {
            // No date changes, proceed with save
            this.performSaveTimeOffRecord();
          }
        },
        error: (error) => {
          console.error("Error loading original event:", error);
          // If we can't load the original, proceed without change tracking
          this.performSaveTimeOffRecord();
        },
      });
    } else {
      // New event, no change tracking needed
      this.performSaveTimeOffRecord();
    }
  }

  private areDatesEqual(date1: Date, date2: Date): boolean {
    if (!date1 || !date2) return false;
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    return (
      d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate()
    );
  }

  public get changeReasonOptions() {
    return DriverEventChangeReasonHelper.getOptions();
  }

  public getChangeReasonLabel(reason: DriverEventChangeReason): string {
    return DriverEventChangeReasonHelper.toString(reason);
  }

  public confirmChangeReason(): void {
    if (!this.pendingUpdate) return;

    // Save the change reason first
    const change: DriverEventChangeViewModel = {
      id: "",
      driverEventId: String(this.pendingUpdate.id),
      previousStart: this.originalStartDate,
      previousEnd: this.originalEndDate,
      newStart: new Date(this.pendingUpdate.startDate),
      newEnd: new Date(this.pendingUpdate.endDate),
      reason: this.changeReason,
      reasonNote: this.changeReasonNote || "",
      dateCreatedUtc: new Date(),
      changedByUserId: "",
    };

    this.driverService.createEventChange(change).subscribe({
      next: () => {
        // Now update the event
        this.timeOffRecord = this.pendingUpdate!;
        this.performSaveTimeOffRecord();
        this.displayChangeDialog = false;
        this.changeReasonNote = "";
        this.changeReason = DriverEventChangeReason.DriverRequested;
        this.pendingUpdate = null;
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error("Error saving change reason:", error);
        this.toastr.error("Failed to save change reason", "Error");
      },
    });
  }

  public cancelChangeReason(): void {
    this.displayChangeDialog = false;
    this.changeReasonNote = "";
    this.changeReason = DriverEventChangeReason.DriverRequested;
    this.pendingUpdate = null;
  }

  private performSaveTimeOffRecord(): void {
    // Convert Date to DateOnly format (YYYY-MM-DD) by creating a new Date with only date components
    const formatDateOnly = (date: Date): Date => {
      const year = date.getFullYear();
      const month = date.getMonth();
      const day = date.getDate();
      return new Date(year, month, day);
    };

    const startDate = formatDateOnly(new Date(this.timeOffRecord.startDate));
    const endDate = formatDateOnly(new Date(this.timeOffRecord.endDate));

    if (!this.selectedDriver) return;
    if (this.isEditingTimeOff) {
      // Update existing time off
      this.dispatchScheduleService
        .updateDriverTimeOff(
          this.timeOffRecord.id,
          this.selectedDriver.driverId,
          startDate,
          endDate,
          this.timeOffRecord.eventType,
          this.timeOffRecord.notes
        )
        .subscribe({
          next: () => {
            this.toastr.success("Time off updated successfully", "Success");
            this.closeTimeOffModal();
            this.loadData(); // Refresh data
            this.cdr.markForCheck();
          },
          error: (error) => {
            this.toastr.error("Failed to update time off", "Error");
            console.error(error);
          },
        });
    } else {
      // Create new time off
      this.dispatchScheduleService
        .createDriverTimeOff(
          this.selectedDriver.driverId,
          startDate,
          endDate,
          this.timeOffRecord.eventType,
          this.timeOffRecord.notes
        )
        .subscribe({
          next: () => {
            this.toastr.success("Time off created successfully", "Success");
            this.closeTimeOffModal();
            this.loadData(); // Refresh data
            this.cdr.markForCheck();
          },
          error: (error) => {
            this.toastr.error("Failed to create time off", "Error");
            console.error(error);
          },
        });
    }
  }

  deleteTimeOffRecord(): void {
    if (!this.timeOffRecord.id) {
      this.toastr.warning("Cannot delete - no time off event ID", "Warning");
      return;
    }

    if (confirm("Are you sure you want to delete this time off event?")) {
      this.dispatchScheduleService.deleteDriverTimeOff(this.timeOffRecord.id).subscribe({
        next: () => {
          this.toastr.success("Time off deleted successfully", "Success");
          this.closeTimeOffModal();
          this.loadData(); // Refresh data
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.toastr.error("Failed to delete time off", "Error");
          console.error(error);
        },
      });
    }
  }

  // ── Column visibility ─────────────────────────────────────────────────────

  private rebuildColumnVisible(): void {
    const map: { [key: string]: boolean } = {};
    this.driverInfoColumns.forEach((col) => (map[col.key] = col.visible));
    this.columnVisible = map;
  }

  get extraColumnSpan(): number {
    return this.driverInfoColumns.filter((c) => c.visible).length;
  }

  loadColumnPreferences(): void {
    this.userPreferencesService.getPreference(this.columnPreferenceKey).subscribe((value) => {
      if (value) {
        try {
          const prefs = (typeof value === "string" ? JSON.parse(value) : value) as Record<
            string,
            boolean
          >;
          this.driverInfoColumns.forEach((col) => {
            if (Object.prototype.hasOwnProperty.call(prefs, col.key)) {
              col.visible = prefs[col.key];
            }
          });
        } catch (error) {
          console.error("Malformed column preferences, resetting to defaults", value, error);
          // Silently ignore malformed preference value
        }
      }
      this.rebuildColumnVisible();
      this.cdr.markForCheck();
    });
    this.rebuildColumnVisible();
  }

  onColumnVisibilityChange(): void {
    this.rebuildColumnVisible();
    const prefs: Record<string, boolean> = {};
    this.driverInfoColumns.forEach((col) => (prefs[col.key] = col.visible));
    this.userPreferencesService
      .savePreference(this.columnPreferenceKey, JSON.stringify(prefs))
      .subscribe();
  }

  // ── Driver Note modal ─────────────────────────────────────────────────────

  openDriverNoteModal(): void {
    if (!this.selectedDriver) return;

    this.driverNoteForModal = {
      driverId: this.selectedDriver.driverId,
      status: "Open",
      note: "",
      dateCreatedUtc: new Date(),
    } as Partial<DriverNote>;

    this.driverViewModelForNoteModal = {
      id: this.selectedDriver.driverId,
      name: this.selectedDriver.name,
      lastName: this.selectedDriver.lastName,
      driverNo: this.selectedDriver.driverNo,
      tractorId: this.selectedDriver.tractorId,
      trailerId: this.selectedDriver.trailerId,
      billToId: this.selectedDriver.billToClientId,
    } as any as DriverViewModel;

    if (!this.noteModalDropdownsLoaded) {
      this.loadNoteModalDropdowns();
    }

    this.closeDateClickModal();
    this.showDriverNoteModal = true;
  }

  private loadNoteModalDropdowns(): void {
    this.newTripService
      .getTractorsDropDown()
      .subscribe((data) => { this.noteModalTractors = (data as any[]) ?? []; this.cdr.markForCheck(); });
    this.newTripService
      .getTrailersDropDown()
      .subscribe((data) => { this.noteModalTrailers = (data as any[]) ?? []; this.cdr.markForCheck(); });
    this.newTripService.getClientsDropDown().subscribe((data) => {
      this.noteModalAllClients = (data as any[]) ?? [];
      this.noteModalClients = this.noteModalAllClients.filter((c: any) => !c.clientId);
      this.cdr.markForCheck();
    });
    this.newTripService
      .getProductsDropDown()
      .subscribe((data) => { this.noteModalProducts = (data as any[]) ?? []; this.cdr.markForCheck(); });
    this.noteModalStates = this.statesService.getTwoLettersStates("US");
    this.noteModalDropdownsLoaded = true;
  }

  onDriverNoteSaved(note: DriverNote): void {
    if (!note.driverId && this.driverViewModelForNoteModal?.id) {
      note.driverId = this.driverViewModelForNoteModal.id;
    }

    // Capture driver name before the modal state is cleared
    const savedDriverId = note.driverId ?? null;
    const savedDriverName = this.driverViewModelForNoteModal
      ? `${this.driverViewModelForNoteModal.name} ${this.driverViewModelForNoteModal.lastName}`
      : null;
    const noteType = note.type ?? '';
    const shouldPromptTimeOff =
      noteType === 'Driver Release' || noteType === 'Accident' || noteType === 'Breakdown & follow up';

    this.driverService.saveNote(note).subscribe({
      next: () => {
        this.showDriverNoteModal = false;
        this.toastr.success("Driver note saved successfully", "Success");

        // If this was a Driver Release note, also remove the driver from their scheduling team
        if (note.type === "Driver Release" && note.driverId) {
          // Use the pending team ID if set (from Unassign button), otherwise look up from current data
          const teamId =
            this.pendingUnassignTeamId ??
            this.schedulingTeamGroups.find((g) =>
              g.drivers.some((d) => d.driver.driverId === note.driverId),
            )?.schedulingTeamId ??
            null;

          this.pendingUnassignTeamId = null;

          if (teamId) {
            const driverId = note.driverId;
            this.dispatchService.removeDriverFromSchedulingTeam(teamId, driverId).subscribe({
              next: () => {
                this.toastr.success("Driver removed from scheduling team", "Unassigned");
                this.loadData();
                if (shouldPromptTimeOff) {
                  this.pendingTimeOffDriverId = savedDriverId;
                  this.pendingTimeOffDriverName = savedDriverName;
                  this.showTimeOffPromptModal = true;
                }
                this.cdr.markForCheck();
              },
              error: () => {
                this.toastr.error("Note saved, but failed to remove driver from team");
                this.loadData();
                if (shouldPromptTimeOff) {
                  this.pendingTimeOffDriverId = savedDriverId;
                  this.pendingTimeOffDriverName = savedDriverName;
                  this.showTimeOffPromptModal = true;
                }
                this.cdr.markForCheck();
              },
            });
          } else {
            this.loadData();
            if (shouldPromptTimeOff) {
              this.pendingTimeOffDriverId = savedDriverId;
              this.pendingTimeOffDriverName = savedDriverName;
              this.showTimeOffPromptModal = true;
            }
            this.cdr.markForCheck();
          }
        } else {
          this.pendingUnassignTeamId = null;
          this.loadData();
          if (shouldPromptTimeOff) {
            this.pendingTimeOffDriverId = savedDriverId;
            this.pendingTimeOffDriverName = savedDriverName;
            this.showTimeOffPromptModal = true;
          }
          this.cdr.markForCheck();
        }
      },
      error: (err) => {
        console.error("Error saving driver note", err);
        this.toastr.error("Failed to save driver note", "Error");
      },
    });
  }

  onDriverNoteModalClosed(): void {
    this.showDriverNoteModal = false;
  }

  // ── Note autosave ─────────────────────────────────────────────────────────

  onNoteChange(driverId: string, note: string | null): void {
    this.noteSaveSubject.next({ driverId, note });
  }

  // ── Time off prompt (after note save) ─────────────────────────────────────

  openTimeOffFromPrompt(): void {
    this.showTimeOffPromptModal = false;
    if (!this.pendingTimeOffDriverId) return;

    // Find the driver row (may be in unassigned after a release)
    const row =
      this.driverRows.find((r) => r.driver.driverId === this.pendingTimeOffDriverId) ??
      this.unassignedDriverRows.find((r) => r.driver.driverId === this.pendingTimeOffDriverId);

    if (row) {
      this.selectedDriver = row.driver;
    } else if (this.pendingTimeOffDriverId) {
      // Construct a minimal driver object so saveTimeOffRecord can use driverId
      this.selectedDriver = {
        id: '',
        driverId: this.pendingTimeOffDriverId,
        name: this.pendingTimeOffDriverName ?? '',
        lastName: '',
        driverNo: '',
        picture: null,
        specialNote: null,
        cellphone: null,
        currentLocation: null,
        parsedLocation: null,
        autoTransmissionOnly: false,
        boardName: null,
        billToClientId: null,
        billToClientName: null,
        boardId: null,
        peopleNetId: null,
        tractorId: null,
        tractorNumber: null,
        trailerId: null,
        trailerNumber: null,
        homeLocation: null,
        teamMembers: null,
        productId: null,
        productName: null,
        lastAvatarSyncDate: null,
      } as SchedulingTeamDriverAssignment;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    this.selectedDate = today;
    this.isEditingTimeOff = false;
    this.timeOffRecord = {
      id: null,
      startDate: today,
      endDate: today,
      eventType: 'TimeOff',
      notes: '',
    };
    this.showTimeOffModal = true;

    this.pendingTimeOffDriverId = null;
    this.pendingTimeOffDriverName = null;
    this.cdr.markForCheck();
  }

  dismissTimeOffPrompt(): void {
    this.showTimeOffPromptModal = false;
    this.pendingTimeOffDriverId = null;
    this.pendingTimeOffDriverName = null;
  }

  // ── Assign to scheduling team ─────────────────────────────────────────────

  openAssignModal(row: DriverAssignmentRow): void {
    this.assignModalRow = row;
    const hasTeam = row.schedulingTeamId && row.schedulingTeamId !== "__unassigned__";
    this.assignModalDriverId = row.driver.driverId;
    this.assignModalExistingTeamId = hasTeam ? row.schedulingTeamId : null;
    this.assignModalExistingBoardId = row.driver.boardId || null;
    this.assignModalExistingBillToId = row.driver.billToClientId || null;
    this.showAssignModal = true;
    this.cdr.markForCheck();
  }

  onAssignOrUpdateSuccess(): void {
    this.loadData();
    this.cdr.markForCheck();
  }

  onUnassignSuccess(): void {
    this.loadData();
    this.cdr.markForCheck();
  }

  onUnassignWithNote(event: { driverId: string; teamId: string }): void {
    // Find the driver row so we can populate the note modal
    const row =
      this.driverRows.find((r) => r.driver.driverId === event.driverId) ??
      this.unassignedDriverRows.find((r) => r.driver.driverId === event.driverId);

    const driver = row?.driver ?? null;

    this.driverNoteForModal = {
      driverId: event.driverId,
      type: "Driver Release",
      status: "Open",
      note: "",
      dateCreatedUtc: new Date(),
    } as Partial<DriverNote>;

    this.driverViewModelForNoteModal = driver
      ? ({
          id: driver.driverId,
          name: driver.name,
          lastName: driver.lastName,
          driverNo: driver.driverNo,
          tractorId: driver.tractorId,
          trailerId: driver.trailerId,
          billToId: driver.billToClientId,
        } as any as DriverViewModel)
      : null;

    this.pendingUnassignTeamId = event.teamId;

    if (!this.noteModalDropdownsLoaded) {
      this.loadNoteModalDropdowns();
    }

    this.showDriverNoteModal = true;
    this.cdr.markForCheck();
  }

  openDriverNoteForView(note: { id: string; type: string; note: string }): void {
    this.dashboardService.getFullDriverNote(note.id).subscribe({
      next: (fullNote) => {
        this.driverNoteForModal = fullNote;

        // Ensure driver ViewModel is set so readonly display works correctly
        if (this.selectedDriver) {
          const viewModel = {
            id: this.selectedDriver.driverId,
            name: this.selectedDriver.name,
            lastName: this.selectedDriver.lastName,
            driverNo: this.selectedDriver.driverNo,
            tractorId: this.selectedDriver.tractorId,
            trailerId: this.selectedDriver.trailerId,
            billToId: this.selectedDriver.billToClientId,
          } as any as DriverViewModel;
          this.driverViewModelForNoteModal = viewModel;
        }

        if (!this.noteModalDropdownsLoaded) {
          this.loadNoteModalDropdowns();
        }

        this.showDriverNoteModal = true;
        this.closeDateClickModal();
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error("Error loading full driver note", err);
        this.toastr.error("Failed to load note details", "Error");
      },
    });
  }
}
